import { Flex, IPropsInterface, Loader, Span } from '@fareye/ui'
import {
  cancelToken,
  durationBetweenDates,
  FaBolt,
  FaDotCircle,
  FaFlag,
  get,
  toIST,
  hhmmAdoMMM,
  isEmpty,
  poiTypeIcon,
  startCase
} from '@fareye/utils'
import React from 'react'
import { useInView } from 'react-intersection-observer'
import { TimeLine, TimeLineContent } from '../../../style/styled'
import FieldValue from '../components/FieldValue'
import { default as Stages } from '../components/Stage'
import { Truck, Rail, Ship, Plane } from '../../../components/Timeline/styled'

const vehicleSize = 25

export interface ITimeLineLine extends IPropsInterface {
  borderColor: 'blue7' | 'gray7'
  borderStyle: 'solid' | 'dotted'
}

const ETATimeLine = React.forwardRef(
  ({ timelineData, etaInfo, baseUrl, id }: any, refs?: any) => {
    const [etaData, setEtaData] = React.useState(() => etaInfo)
    const [loading, setLoading] = React.useState(() => !!etaInfo)

    const [ref, inView, entry] = useInView({
      /* Optional options */
      threshold: 0,
      triggerOnce: true
    })
    const source = cancelToken().source()

    const originalData = timelineData

    const totalPoisAllowedOnTimeline = 2
    const timelineMinWidthPerStop =
      originalData.tripCompletionPerc / totalPoisAllowedOnTimeline
    const lastStopWidth = 100 - originalData.tripCompletionPerc
    async function fetchTimeline(slug: string) {
      try {
        setLoading(true)
        const {
          data: { content }
        } = await get(
          `${baseUrl}/api/v2/consigner_trips/eta_info?slug[]=${slug}`,
          source.token
        )
        const allPois = content[originalData.slug]
        const last2Pois = allPois.slice(
          allPois.length - totalPoisAllowedOnTimeline,
          allPois.length
        )
        setEtaData(last2Pois)
        setLoading(false)
      } catch (err:any) {
        setLoading(false)
        console.error('App Error:', err)
      }
    }

    React.useEffect(() => {
      if (inView) {
        !etaInfo && fetchTimeline(originalData.slug)
      } else {
        // source.cancel()
      }
      // return source.cancel()
    }, [inView])

    const stoppagesIns = originalData.stoppageInstances
    const overspeedingIns = originalData.overspeedingInstances
    let stoppages = originalData && originalData.stoppages
    let overspeeding = originalData && originalData.overspeeding

    const iconSize = 15
    const endIconSize = iconSize
    const iconStyle: React.CSSProperties = {
      border: '1px solid grey',
      margin: '10px 0 5px',
      borderRadius: '50%',
      background: '#fff'
    }
    const EndStageNameStyles = {
      fontWeight: 700,
      fontSize: 14,
      marginBottom: -5
    }
    const hasEtaPOIs = etaData && etaData.length > 0
    const stoppagesFiltered =
      !isEmpty(stoppages[`-${hasEtaPOIs && etaData[0].poi_reached_id}`]) &&
      stoppages[`-${hasEtaPOIs && etaData[0].poi_reached_id}`].filter(
        (item: any) => item.duration
      )
    const stoppagesBetweenOD = stoppagesIns.filter((item: any) => item.duration)
    const showTimeline =
      hasEtaPOIs || !isEmpty(stoppagesIns) || !isEmpty(overspeedingIns)
    if (!showTimeline) {
      // return <div ref={ref} />
    }
    const getVehicleIcon = ( transportMode: string ): any => {
      switch (transportMode) {
        case 'road':
          return Truck
        case 'rail':
          return Rail
        case 'ocean':
          return Ship
        case 'air':
          return Plane
        default:
          return Truck
      }
    }
    // vehicle Icon based on transportMode
    const { transportMode = 'road' } = originalData
    const VehicleIcon =  getVehicleIcon(transportMode)
    // Vehicle position 25px reduced on n above 97% to keep vehicle icon under div and not showing scrollbar
    const vehiclePositionUnderViewPort = `calc(${originalData.tripCompletionPerc ||
      0}% - ${originalData.tripCompletionPerc > 97 ? vehicleSize / 2 : 0}px)`

    return (
      <TimeLine id={originalData.slug} ref={ref}>
        {loading ? (
          <div style={{ padding: '45px', width: '100%', textAlign: 'center' }}>
            <Loader height="20px" width="20px" variant="spin" />
          </div>
        ) : (
          <>
            <Flex column alignItemsFlexEnd style={{ width: 135 }}>
              <Span
                variant="ellipsis"
                style={{ ...EndStageNameStyles, textAlign: 'right' }}
              >
                {originalData.origin}
              </Span>
              <FaBolt
                style={{ ...iconStyle, padding: 3 }}
                size={endIconSize - 2}
              />
              <FieldValue
                style={{ marginTop: 2, width: '100%' }}
                label="In:"
                value={
                  originalData.loadingInTime
                    ? hhmmAdoMMM(originalData.loadingInTime)
                    : '-'
                }
              />
              <FieldValue
                style={{ marginBottom: 0, width: '100%' }}
                label="Out:"
                value={
                  originalData.loadingOutTime
                    ? hhmmAdoMMM(originalData.loadingOutTime)
                    : '-'
                }
              />
            </Flex>
            <TimeLineContent>
              <Stages
                style={{
                  stage: {
                    width: hasEtaPOIs ? `${timelineMinWidthPerStop}%` : '100%',
                    paddingBottom: 7
                  },
                  iconWrapper: {
                    left: vehiclePositionUnderViewPort,
                    transform: 'translateX(-50%)'
                  },
                  iconLine: { height: endIconSize }
                }}
                line={{
                  color: hasEtaPOIs ? 'blue7' : 'gray5',
                  style: hasEtaPOIs ? 'solid' : 'dotted',
                  width:
                    !originalData.loadingOutTime || etaData.length > 0
                      ? null
                      : originalData.tripCompletionPerc
                }}
                stoppages={
                  (hasEtaPOIs ? stoppagesFiltered : stoppagesBetweenOD) || []
                }
                icon={
                  originalData.loadingOutTime && isEmpty(etaData) ? (
                    <VehicleIcon style={{ zIndex: 0 }} size={vehicleSize} />
                  ) : (
                    <></>
                  )
                }
                overspeeding={
                  hasEtaPOIs
                    ? !isEmpty(
                        overspeeding[
                          `-${hasEtaPOIs && etaData[0].poi_reached_id}`
                        ]
                      )
                    : overspeedingIns.length
                }
              />
              {etaData &&
                etaData.map((item: any, i: number) => {
                  const isLastIteration = i == etaData.length - 1
                  const poiId =
                    !isLastIteration &&
                    `${item.poi_reached_id}-${etaData[i + 1].poi_reached_id}`
                  const hasStoppages = (poiId && stoppages[poiId]) || []
                  const hasOverspeeding =
                    poiId && overspeeding[poiId] && overspeeding[poiId].length
                  const Icon = poiTypeIcon[item.poi_type]
                  return (
                    <Stages
                      key={i}
                      style={{
                        stage: {
                          width: `${
                            isLastIteration
                              ? lastStopWidth
                              : timelineMinWidthPerStop
                          }%`
                        }
                      }}
                      name={startCase(item.poi_name)}
                      eta={`${hhmmAdoMMM(toIST(item.predicted_eta_from_here))}`}
                      tripRunningStatus={durationBetweenDates({
                        startDate: originalData.eta,
                        endDate: item.predicted_eta_from_here
                      })}
                      poiType={item.poi_type}
                      icon={
                        isLastIteration &&
                        !originalData.ata &&
                        originalData.tripCompletionPerc != 100 ? (
                          <VehicleIcon size={25} />
                        ) : (
                          <Icon
                            title={startCase(item.poi_type)}
                            size={iconSize}
                            style={{ padding: '4px 0', marginLeft: '-4px' }}
                          />
                        )
                      }
                      line={{
                        color: `${
                          !originalData.ata && isLastIteration
                            ? 'gray7'
                            : 'blue7'
                        }`,
                        style: `${
                          !originalData.ata && isLastIteration
                            ? 'dotted'
                            : 'solid'
                        }`,
                        width:
                          originalData.tripCompletionPerc == 100 ? 100 : null
                      }}
                      stoppages={!isLastIteration && hasStoppages}
                      overspeeding={!isLastIteration && hasOverspeeding}
                      enterDatetime={hhmmAdoMMM(item.poi_enter_time)}
                      exitDatetime={hhmmAdoMMM(item.poi_exit_time)}
                    />
                  )
                })}
              <Stages
                style={{ stage: { width: 0, minWidth: 0, flexGrow: 0 } }}
              />
            </TimeLineContent>
            <Flex column style={{ width: 140 }} alignItemsFlexStart>
              <Span variant="ellipsis" style={EndStageNameStyles}>
                {originalData.consigneeName}
              </Span>
              <FaFlag
                style={{
                  ...iconStyle,
                  padding: '5px 3px',
                  height: endIconSize - 4,
                  width: endIconSize
                }}
              />
              <Flex column alignItemsCenter>
                <FieldValue
                  style={{ marginBottom: 0, width: '100%' }}
                  label={originalData.ata ? null : 'ETA:'}
                  value={
                    originalData.ata
                      ? null
                      : hhmmAdoMMM(originalData.rtEta || originalData.eta)
                  }
                />
                <FieldValue
                  style={{ marginTop: 2, width: '100%' }}
                  label={originalData.ata ? 'ATA:' : 'STA:'}
                  value={hhmmAdoMMM(originalData.ata || originalData.eta)}
                />
              </Flex>
            </Flex>
          </>
        )}
      </TimeLine>
    )
  }
)

export default ETATimeLine
